import React, { useState, useEffect } from 'react'
import { parseDateString } from '../utils/dateUtils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Loader2, FileText, Package, AlertCircle, CheckCircle2, XCircle, Clock } from 'lucide-react'
import {
  fetchImportSessionDetail,
  fetchImportSessionCSVData,
  fetchImportSessionSalesRecords,
  fetchImportSessionStatistics
} from '../supabase-client'

/**
 * Import History Detail View Component
 * Displays detailed information about a completed import session with two tabs:
 * 1. CSV Preview - Shows original CSV data with import status indicators
 * 2. Imported Sales - Shows the actual sales records created from the import
 */
export const ImportHistoryDetailView = ({ sessionId, onClose }) => {
  const [activeTab, setActiveTab] = useState('csv')
  const [loading, setLoading] = useState(true)
  const [sessionDetail, setSessionDetail] = useState(null)
  const [csvData, setCsvData] = useState({ records: [], totalCount: 0 })
  const [salesData, setSalesData] = useState({ records: [], totalCount: 0 })
  const [error, setError] = useState(null)

  // CSV pagination
  const [csvPage, setCsvPage] = useState(0)
  const [csvLoading, setCsvLoading] = useState(false)
  const CSV_PAGE_SIZE = 50

  // Sales pagination
  const [salesPage, setSalesPage] = useState(0)
  const [salesLoading, setSalesLoading] = useState(false)
  const SALES_PAGE_SIZE = 20

  // Load session detail and initial data
  useEffect(() => {
    loadSessionDetail()
  }, [sessionId])

  // Load CSV data when tab changes to CSV
  useEffect(() => {
    if (activeTab === 'csv' && csvData.records.length === 0) {
      loadCSVData()
    }
  }, [activeTab])

  // Load sales data when tab changes to Sales
  useEffect(() => {
    if (activeTab === 'sales' && salesData.records.length === 0) {
      loadSalesData()
    }
  }, [activeTab])

  const loadSessionDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      const detail = await fetchImportSessionDetail(sessionId)
      setSessionDetail(detail)
    } catch (err) {
      console.error('[ImportHistoryDetailView] Error loading session detail:', err)
      setError('Failed to load import session details')
    } finally {
      setLoading(false)
    }
  }

  const loadCSVData = async (page = 0) => {
    try {
      setCsvLoading(true)
      const offset = page * CSV_PAGE_SIZE
      const data = await fetchImportSessionCSVData(sessionId, CSV_PAGE_SIZE, offset)
      setCsvData(data)
      setCsvPage(page)
    } catch (err) {
      console.error('[ImportHistoryDetailView] Error loading CSV data:', err)
      setError('Failed to load CSV preview data')
    } finally {
      setCsvLoading(false)
    }
  }

  const loadSalesData = async (page = 0) => {
    try {
      setSalesLoading(true)
      const offset = page * SALES_PAGE_SIZE
      const data = await fetchImportSessionSalesRecords(sessionId, SALES_PAGE_SIZE, offset)
      setSalesData(data)
      setSalesPage(page)
    } catch (err) {
      console.error('[ImportHistoryDetailView] Error loading sales data:', err)
      setError('Failed to load imported sales records')
    } finally {
      setSalesLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      valid: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Valid' },
      invalid: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid' },
      warning: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Warning' },
      pending: { color: 'bg-gray-100 text-gray-800', icon: Clock, label: 'Pending' }
    }

    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    )
  }

  const getImportStatusIndicator = (record) => {
    if (record.imported) {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Imported
        </Badge>
      )
    }

    if (record.isDuplicate) {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Duplicate
        </Badge>
      )
    }

    if (record.validation_status === 'invalid') {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Skipped
        </Badge>
      )
    }

    return (
      <Badge className="bg-gray-100 text-gray-800">
        Not Imported
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!sessionDetail) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Not Found</AlertTitle>
        <AlertDescription>Import session not found</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Session Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {sessionDetail.file_name}
            </CardTitle>
            <code className="text-xs bg-gray-100 px-3 py-1.5 rounded text-gray-600 font-mono">
              ID: {sessionId.substring(0, 8).toUpperCase()}
            </code>
          </div>
          <CardDescription>
            Imported on {new Date(sessionDetail.created_at).toLocaleDateString()} at{' '}
            {new Date(sessionDetail.created_at).toLocaleTimeString()}
            {sessionDetail.completed_at && (
              <>
                {' '}• Completed on {new Date(sessionDetail.completed_at).toLocaleDateString()} at{' '}
                {new Date(sessionDetail.completed_at).toLocaleTimeString()}
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Records</div>
              <div className="text-2xl font-semibold">{sessionDetail.stagingRecordCount}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Imported</div>
              <div className="text-2xl font-semibold text-green-600">
                {sessionDetail.importedRecordCount}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Skipped</div>
              <div className="text-2xl font-semibold text-gray-600">
                {sessionDetail.stagingRecordCount - sessionDetail.importedRecordCount}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Duplicates Detected</div>
              <div className="text-2xl font-semibold text-yellow-600">
                {sessionDetail.duplicateStats.disregarded}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="csv" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            CSV Preview
          </TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Imported Sales ({sessionDetail.importedRecordCount})
          </TabsTrigger>
        </TabsList>

        {/* CSV Preview Tab */}
        <TabsContent value="csv" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Original CSV Data</CardTitle>
              <CardDescription>
                Showing {csvData.records.length} of {csvData.totalCount} records
              </CardDescription>
            </CardHeader>
            <CardContent>
              {csvLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : csvData.records.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No CSV data available for this import session.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* CSV Data Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Row</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Customer</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Product</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Quantity</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Price</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700">Validation</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {csvData.records.map((record) => {
                            const originalData = record.original_data || {}
                            return (
                              <tr
                                key={record.id}
                                className={`hover:bg-gray-50 ${
                                  record.imported
                                    ? 'bg-green-50'
                                    : record.isDuplicate
                                    ? 'bg-yellow-50'
                                    : record.validation_status === 'invalid'
                                    ? 'bg-red-50'
                                    : ''
                                }`}
                              >
                                <td className="px-4 py-3 text-gray-900">{record.row_number}</td>
                                <td className="px-4 py-3">{getImportStatusIndicator(record)}</td>
                                <td className="px-4 py-3 text-gray-900">
                                  {originalData.Date || originalData.date || '-'}
                                </td>
                                <td className="px-4 py-3 text-gray-900">
                                  {originalData.Customer || originalData.customer || '-'}
                                </td>
                                <td className="px-4 py-3 text-gray-900">
                                  {(originalData.Strain || originalData.strain || '') +
                                    ' ' +
                                    (originalData['Product Type'] ||
                                      originalData.product ||
                                      '')}
                                </td>
                                <td className="px-4 py-3 text-gray-900">
                                  {originalData['Quantity (Kg/Units)'] ||
                                    originalData.quantity ||
                                    '-'}
                                </td>
                                <td className="px-4 py-3 text-gray-900">
                                  £
                                  {originalData['Price (£/Kg/Unit)'] ||
                                    originalData.price ||
                                    '-'}
                                </td>
                                <td className="px-4 py-3">
                                  {getStatusBadge(record.validation_status)}
                                  {record.validation_errors &&
                                    record.validation_errors.length > 0 && (
                                      <div className="text-xs text-red-600 mt-1">
                                        {record.validation_errors.join(', ')}
                                      </div>
                                    )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* CSV Pagination */}
                  {csvData.totalCount > CSV_PAGE_SIZE && (
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        onClick={() => loadCSVData(csvPage - 1)}
                        disabled={csvPage === 0 || csvLoading}
                      >
                        Previous
                      </Button>
                      <div className="text-sm text-gray-600">
                        Page {csvPage + 1} of {Math.ceil(csvData.totalCount / CSV_PAGE_SIZE)}
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => loadCSVData(csvPage + 1)}
                        disabled={
                          (csvPage + 1) * CSV_PAGE_SIZE >= csvData.totalCount || csvLoading
                        }
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Imported Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Imported Sales Records</CardTitle>
              <CardDescription>
                Sales records created from this import in Odin's database
              </CardDescription>
            </CardHeader>
            <CardContent>
              {salesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : salesData.records.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No sales records were imported from this session.</p>
                  <p className="text-sm mt-2">This might be due to validation errors or duplicates.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Sales Records List */}
                  <div className="space-y-3">
                    {salesData.records.map((sale) => (
                      <Card key={sale.id} className="bg-white shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="font-semibold text-lg">
                                {sale.customers?.name || 'Unknown Customer'}
                                {sale.branches?.name && (
                                  <span className="text-sm text-gray-600 ml-2">
                                    ({sale.branches.name})
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                {parseDateString(sale.date).toLocaleDateString()}
                                {sale.consignment_no && ` • Consignment: ${sale.consignment_no}`}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={`${
                                  sale.status === 'delivered'
                                    ? 'bg-green-100 text-green-800'
                                    : sale.status === 'packed'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {sale.status}
                              </Badge>
                              {sale.invoiced && (
                                <Badge className="bg-purple-100 text-purple-800">
                                  Invoiced
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Sale Items */}
                          {sale.sale_items && sale.sale_items.length > 0 && (
                            <div className="space-y-2 mt-3 border-t pt-3">
                              <div className="text-sm font-medium text-gray-700">Items:</div>
                              {sale.sale_items.map((item) => (
                                <div
                                  key={item.id}
                                  className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded"
                                >
                                  <div className="flex-1">
                                    <span className="font-medium">
                                      {item.products?.strain} {item.products?.type}
                                    </span>
                                    {item.discount_percent > 0 && (
                                      <Badge className="ml-2 bg-orange-100 text-orange-800">
                                        {item.discount_percent}% off
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-gray-700">
                                    <span>
                                      {item.quantity} kg × £{item.price}
                                    </span>
                                    <span className="font-semibold">
                                      £{Number(item.sub_total).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              <div className="flex justify-end text-base font-semibold pt-2 border-t">
                                Total: £
                                {sale.sale_items
                                  .reduce((sum, item) => sum + Number(item.sub_total), 0)
                                  .toFixed(2)}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Sales Pagination */}
                  {salesData.totalCount > SALES_PAGE_SIZE && (
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        onClick={() => loadSalesData(salesPage - 1)}
                        disabled={salesPage === 0 || salesLoading}
                      >
                        Previous
                      </Button>
                      <div className="text-sm text-gray-600">
                        Page {salesPage + 1} of {Math.ceil(salesData.totalCount / SALES_PAGE_SIZE)}
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => loadSalesData(salesPage + 1)}
                        disabled={
                          (salesPage + 1) * SALES_PAGE_SIZE >= salesData.totalCount ||
                          salesLoading
                        }
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ImportHistoryDetailView
